import React, { useEffect, useState } from 'react';
import API from '../../utils/api';
import { formatDateTime } from '../../utils/config';
import { FaUsers, FaCheckCircle, FaClock, FaTimesCircle, FaArrowRight, FaLeaf, FaUserPlus } from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';

export default function DashboardHome({ onStatsLoad }) {
  const [stats, setStats] = useState({ total: 0, approved: 0, pending: 0, rejected: 0, recentWeek: 0 });
  const [recentMembers, setRecentMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [statsRes, membersRes] = await Promise.all([
        API.get('/members/stats/summary'),
        API.get('/members?limit=5')
      ]);
      setStats(statsRes.data);
      setRecentMembers(membersRes.data.members || []);
      onStatsLoad?.(statsRes.data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const statCards = [
    { label: 'Total Members', value: stats.total, icon: '👥', color: 'green', sub: `+${stats.recentWeek} this week` },
    { label: 'Approved', value: stats.approved, icon: '✅', color: 'green', sub: 'Active members' },
    { label: 'Pending', value: stats.pending, icon: '⏳', color: 'orange', sub: 'Awaiting review' },
    { label: 'Rejected', value: stats.rejected, icon: '❌', color: 'red', sub: 'Not approved' },
  ];

  const now = new Date();
  const dateStr = now.toLocaleDateString('en-IN', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' });

  return (
    <div>
      {/* Topbar */}
      <div className="topbar">
        <div>
          <h1 style={{ fontSize: 20, fontWeight: 700, color: '#111827', fontFamily: 'Poppins, sans-serif' }}>Dashboard</h1>
          <p style={{ fontSize: 13, color: '#6b7280', marginTop: 2 }}>{dateStr}</p>
        </div>
        <div style={{ display: 'flex', gap: 10 }}>
          <button className="btn btn-primary btn-sm" onClick={() => navigate('/admin/register')}>
            <FaUserPlus size={13} /> New Registration
          </button>
        </div>
      </div>

      <div className="page-content">
        {/* Welcome Banner */}
        <div style={{
          background: 'linear-gradient(135deg, #15803d 0%, #22c55e 100%)',
          borderRadius: 16, padding: '24px 28px', marginBottom: 28,
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          color: 'white', position: 'relative', overflow: 'hidden'
        }}>
          <div style={{ position: 'absolute', right: -20, top: -20, fontSize: 120, opacity: 0.08 }}>🌿</div>
          <div>
            <div style={{ fontSize: 22, fontWeight: 700, fontFamily: 'Poppins, sans-serif', marginBottom: 6 }}>
              Sri Karunasai Medicinal Plantation Association
            </div>
            <div style={{ opacity: 0.9, fontSize: 14 }}>Managing memberships and cultivating a greener future</div>
          </div>
          <div style={{ textAlign: 'right', flexShrink: 0, marginLeft: 24 }}>
            <div style={{ fontSize: 38, fontWeight: 800, fontFamily: 'Poppins, sans-serif' }}>{stats.total}</div>
            <div style={{ fontSize: 13, opacity: 0.85 }}>Total Members</div>
          </div>
        </div>

        {/* Stats Grid */}
        <div className="dashboard-stats-grid">
          {statCards.map((s, i) => (
            <div key={i} className={`stat-card ${s.color}`}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
                <div style={{ fontSize: 28 }}>{s.icon}</div>
                <div style={{ fontSize: 32, fontWeight: 800, color: '#111827', fontFamily: 'Poppins, sans-serif' }}>{s.value}</div>
              </div>
              <div style={{ fontWeight: 600, fontSize: 14, color: '#374151' }}>{s.label}</div>
              <div style={{ fontSize: 12, color: '#9ca3af', marginTop: 2 }}>{s.sub}</div>
            </div>
          ))}
        </div>

        {/* Recent Members */}
        <div className="card">
          <div className="card-header">
            <div>
              <h3 style={{ fontWeight: 700, fontSize: 16 }}>Recent Registrations</h3>
              <p style={{ fontSize: 13, color: '#6b7280' }}>Latest member registrations</p>
            </div>
            <button className="btn btn-outline btn-sm" onClick={() => navigate('/admin/registrations')}>
              View All <FaArrowRight size={12} />
            </button>
          </div>
          <div className="table-container">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Member ID</th>
                  <th>Name</th>
                  <th>Mobile</th>
                  <th>District</th>
                  <th>Category</th>
                  <th>Status</th>
                  <th>Date</th>
                </tr>
              </thead>
              <tbody>
                {recentMembers.length === 0 ? (
                  <tr><td colSpan={7} style={{ textAlign: 'center', padding: '40px', color: '#9ca3af' }}>
                    No registrations yet. <span style={{ color: '#16a34a', cursor: 'pointer' }} onClick={() => navigate('/admin/register')}>Add one →</span>
                  </td></tr>
                ) : recentMembers.map(m => (
                  <tr key={m.id}>
                    <td><span style={{ fontFamily: 'monospace', color: '#16a34a', fontWeight: 600 }}>{m.member_id}</span></td>
                    <td style={{ fontWeight: 600 }}>{m.name}</td>
                    <td>{m.mobile_phone || '—'}</td>
                    <td>{m.district || '—'}</td>
                    <td>{m.category || '—'}</td>
                    <td>
                      <span className={`badge ${m.status === 'approved' ? 'badge-success' : m.status === 'pending' ? 'badge-pending' : 'badge-danger'}`}>
                        {m.status}
                      </span>
                    </td>
                    <td style={{ color: '#6b7280', fontSize: 12, whiteSpace: 'nowrap' }}>
                      {(() => {
                        const dt = formatDateTime(m.created_at);
                        return (
                          <div>
                            <div style={{ fontWeight: 600, color: '#111827' }}>{dt.date}</div>
                            {dt.time && (
                              <div style={{ fontSize: 11, color: '#6b7280', marginTop: 1 }}>
                                {dt.time}
                              </div>
                            )}
                          </div>
                        );
                      })()}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Quick Actions */}
        <div className="dashboard-quick-actions-grid">
          {[
            { icon: '📝', label: 'New Registration', sub: 'Add a new member', action: () => navigate('/admin/register'), color: '#16a34a' },
            { icon: '📊', label: 'Export CSV', sub: 'Download all members', action: async () => {
              try {
                const res = await API.get('/members/export/csv', { responseType: 'blob' });
                const url = window.URL.createObjectURL(new Blob([res.data], { type: 'text/csv' }));
                const link = document.createElement('a');
                link.href = url;
                link.setAttribute('download', `members_${new Date().toISOString().slice(0, 10)}.csv`);
                document.body.appendChild(link);
                link.click();
                link.remove();
                window.URL.revokeObjectURL(url);
              } catch (err) {
                console.error('Failed to export CSV', err);
              }
            }, color: '#3b82f6' },
            { icon: '💬', label: 'Open Chat', sub: 'Message with members', action: () => navigate('/admin/chat'), color: '#f59e0b' },
          ].map((a, i) => (
            <div key={i} className="card dashboard-quick-action-card"
              onClick={a.action}
            >
              <div className="action-icon">{a.icon}</div>
              <div className="action-title">{a.label}</div>
              <div className="action-sub">{a.sub}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
